/**
 * StepRow — A single collapsed step row.
 * Shows: drag handle, status icon, step number, type chip, description, expand chevron.
 * Matches the WatchModeMockup step row style.
 */

import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import {
  CheckCircle as PassIcon,
  Cancel as FailIcon,
  HourglassEmpty as RunningIcon,
  Circle as PendingIcon,
  DragIndicator as DragIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { getStepTypeKey, getStepSummary, TYPE_COLORS, STEP_TEMPLATES } from './stepTypes';

// Status rendering configs
const STATUS_CONFIGS = {
  passed:  { icon: <PassIcon sx={{ fontSize: 18, color: '#4caf50' }} />, bg: '#f1f8e9' },
  failed:  { icon: <FailIcon sx={{ fontSize: 18, color: '#f44336' }} />, bg: '#ffebee' },
  running: { icon: <RunningIcon sx={{ fontSize: 18, color: '#ff9800' }} />, bg: '#fff8e1', border: '2px solid #ffc107' },
  pending: { icon: <PendingIcon sx={{ fontSize: 14, color: '#bdbdbd' }} />, bg: 'transparent' },
};

const StepRow = ({
  step,
  index,
  status = 'pending',
  isExpanded,
  onToggleExpand,
  dragHandleProps,
}) => {
  const typeKey = getStepTypeKey(step);
  const topLevelType = typeKey.split('.')[0];
  const colors = TYPE_COLORS[topLevelType] || TYPE_COLORS.action;
  const template = STEP_TEMPLATES[typeKey];
  const label = template?.label || step.type;
  const sc = STATUS_CONFIGS[status] || STATUS_CONFIGS.pending;

  return (
    <Box
      onClick={onToggleExpand}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 1,
        borderRadius: 1,
        cursor: 'pointer',
        mb: 0.5,
        bgcolor: sc.bg,
        border: sc.border || '1px solid transparent',
        '&:hover': {
          bgcolor: status === 'pending' ? '#f5f5f5' : sc.bg,
        },
      }}
    >
      {/* Drag handle */}
      <Box
        {...(dragHandleProps || {})}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: '#9e9e9e',
          '&:hover': { color: '#616161' },
          mr: -0.5,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIcon sx={{ fontSize: 18 }} />
      </Box>

      {/* Status icon */}
      {sc.icon}

      {/* Step number */}
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: '#757575', minWidth: 20 }}
      >
        {step.stepNumber || index + 1}
      </Typography>

      {/* Type chip */}
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: colors.bg,
          color: colors.text,
          fontWeight: 600,
          fontSize: '0.65rem',
          height: 20,
          textTransform: 'capitalize',
          borderColor: colors.border,
        }}
        variant="outlined"
      />

      {/* Description / summary */}
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          fontWeight: status === 'running' ? 700 : 400,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {step.name || getStepSummary(step)}
      </Typography>

      {/* Expand / collapse chevron */}
      {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
    </Box>
  );
};

export default StepRow;
